/**
 * Il QR si inquadra solo di persona: chi invita da lontano ha bisogno di un
 * testo da incollare in chat o di un'immagine da allegare. Qui stanno le due
 * conversioni e i loro ripieghi, perché nessuna delle due è garantita da tutti
 * i browser e la condivisione non può dipendere dal permesso di appunti.
 */

/** Il QR a schermo è 180 px: troppo pochi perché un altro telefono lo rilegga. */
const EXPORT_SIZE = 512;
/** Zona di rispetto: senza margine bianco il lettore perde il QR sul fondo della chat. */
const QUIET_ZONE = 32;

export type CopyOutcome = "copied" | "downloaded" | "failed";

/**
 * Il campo di testo è il ripiego, non un dettaglio dell'interfaccia: fuori da
 * un contesto sicuro `navigator.clipboard` non esiste, e la selezione con
 * `execCommand` resta l'unica copia che quei browser accettano.
 */
export async function copyInviteLink(
  url: string,
  field?: HTMLTextAreaElement | null,
): Promise<CopyOutcome> {
  try {
    await navigator.clipboard.writeText(url);
    return "copied";
  } catch {
    return copyBySelection(field) ? "copied" : "failed";
  }
}

/**
 * Se gli appunti rifiutano le immagini il PNG viene scaricato: allegarlo da
 * galleria costa un tocco in più, ma l'invito arriva lo stesso.
 */
export async function copyInviteQr(svg: SVGSVGElement | null): Promise<CopyOutcome> {
  if (!svg) return "failed";
  const png = qrPngBlob(svg);
  if (typeof ClipboardItem !== "undefined" && typeof navigator.clipboard?.write === "function") {
    try {
      // La promessa va consegnata al costruttore, non attesa prima: Safari
      // considera scaduto il gesto dell'utente se la scrittura arriva dopo.
      await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
      return "copied";
    } catch {
      // Appunti chiusi alle immagini: resta il download.
    }
  }
  try {
    downloadBlob(await png, "invito-palestra.png");
    return "downloaded";
  } catch {
    return "failed";
  }
}

function copyBySelection(field?: HTMLTextAreaElement | null): boolean {
  if (!field) return false;
  field.focus();
  field.select();
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  }
}

async function qrPngBlob(svg: SVGSVGElement): Promise<Blob> {
  const source = new XMLSerializer().serializeToString(svg);
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.addEventListener("load", () => resolve());
    image.addEventListener("error", () => reject(new Error("QR non convertibile in immagine")));
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`;
  });
  const side = EXPORT_SIZE + QUIET_ZONE * 2;
  const canvas = document.createElement("canvas");
  canvas.width = side;
  canvas.height = side;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas non disponibile");
  // Il fondo bianco è parte del codice, non decorazione: senza, il PNG eredita
  // la trasparenza e su una chat scura il QR diventa illeggibile.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, side, side);
  context.drawImage(image, QUIET_ZONE, QUIET_ZONE, EXPORT_SIZE, EXPORT_SIZE);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("PNG non generato"))),
      "image/png",
    );
  });
}

function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  // Revocare subito annullerebbe il download appena avviato in alcuni browser.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
